import config from 'next/config';

import type { BreadCrumbElementProps } from '@components/BreadCrumb/BreadCrumbElement';
import type { LayerInfo } from '@lib/types';

export const formatPrice = (
  price: number | string | undefined | null,
  fractionDigits = 0,
  style: Intl.NumberFormatOptions['style'] = 'decimal',
  transformer: (value: number) => number = (v) => v,
  locale = 'de-DE',
): string => {
  if (price === undefined || price === null) {
    return '';
  }

  const parsedPrice = typeof price === 'number' ? price : parseFloat(price);

  return new Intl.NumberFormat(locale, {
    style,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(transformer(parsedPrice));
};

export function formatDateString(date: string | Date, onlyYear = false): string {
  const parsedDate = new Date(date);

  if (onlyYear) {
    return `${parsedDate.getFullYear()}`;
  }

  return `${(parsedDate.getMonth() + 1).toString().padStart(2, '0')}/${parsedDate.getFullYear()}`;
}

export const defined = <T>(x: T): x is NonNullable<T> => x != null;

export function random(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

export function removeMultipleArrayValuesByKey<T extends Record<string, string>>(
  arr: T[],
  key: string,
): T[] {
  const newArray: T[] = [];
  const temp: string[] = [];

  arr.forEach((x) => {
    if (temp.indexOf(encodeURIComponent(x[key])) === -1) {
      newArray.push(x);
    }

    temp.push(encodeURIComponent(x[key]));
  });

  return newArray;
}

export function mkAssetUrl(url: string | null | undefined = ''): string {
  const sanitizedUrl = !url ? '' : url.replace(/\/$/, '');
  return config().publicRuntimeConfig.assetPrefix + sanitizedUrl || '/';
}

export function removeDuplicateSiblings<T>(array: T[]): T[] {
  return array.reduce<T[]>((acc, element) => {
    const lastProcessedElement = acc[acc.length - 1];
    if (lastProcessedElement !== element) {
      acc.push(element);
    }
    return acc;
  }, []);
}

export function generateBreadCrumbPropsFromLayer(layer: LayerInfo): BreadCrumbElementProps[] {
  const layerTexts = removeDuplicateSiblings([
    layer.state,
    layer.city,
    layer.district,
    layer.districtSmall,
  ]);

  const layerSlugs = removeDuplicateSiblings([
    layer.stateSlug,
    layer.citySlug,
    layer.districtSlug,
    layer.districtSmallSlug,
  ]);

  const breadCrumbProps: BreadCrumbElementProps[] = [];

  layerTexts.forEach((element, index) => {
    if (element) {
      breadCrumbProps.push({
        text: element,
        link: layerSlugs[index],
      });
    }
  });

  return breadCrumbProps;
}

export function breadCrumbElementsProps(
  paths: BreadCrumbElementProps[],
  removeLastLink = true,
): BreadCrumbElementProps[] {
  const parts = paths.filter((part) => !!part);

  const breadCrumbElementsProps = parts.reduce<BreadCrumbElementProps[]>(
    (breadCrumbProps, part, i) => {
      const previousUrlPartLink =
        (i !== 0 && breadCrumbProps[breadCrumbProps.length - 1].link) || '';

      if (part.text) {
        breadCrumbProps.push({
          text: decodeURIComponent(part.text),
          link: part.link && `${previousUrlPartLink}/${part.link}`,
        });
      }

      return breadCrumbProps;
    },
    [
      {
        text: 'Immobilienpreise in Deutschland',
        link: '/',
      },
    ],
  );

  if (removeLastLink) {
    delete breadCrumbElementsProps[breadCrumbElementsProps.length - 1].link;
  }

  return breadCrumbElementsProps;
}

export const unregisterServiceWorkers = (): void => {
  if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        registration.unregister();
      }
    });
  }
};

export function sanitizeQueryParam(param?: string | string[]): string | undefined {
  return Array.isArray(param) ? param[0] : param;
}

export const getStreetNameFromAddress = (address = ''): string | null => {
  const addressDetails = address.split(', ');
  return Number.isNaN(parseInt(addressDetails[0])) ? addressDetails[0] : null;
};
